import { SESSION_STORAGE_KEYS } from '@src/constants/sessionStorage';
import { NavigateFunction } from 'react-router-dom';

/**
 * Get allowed domains from environment variable
 * Format: JSON array ["domain1.com", ".domain2.com"] or comma-separated string
 */
const getAllowedDomains = (): string[] => {
  const allowedDomains = import.meta.env.VITE_ALLOWED_REDIRECT_DOMAINS;

  if (allowedDomains) {
    try {
      const parsed = JSON.parse(allowedDomains);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      return allowedDomains.split(',').map((domain: string) => domain.trim());
    }
  }

  return ['dxstar.jp', '.dxstar.jp'];
};

/**
 * Validate redirect URL to prevent open redirect attacks
 * Only allows relative URLs or configured trusted domains
 */
export const isValidDxstarUrl = (url: string): boolean => {
  try {
    if (url.startsWith('/')) {
      return true;
    }

    const urlObj = new URL(url);
    const currentDomain = window.location.hostname;
    const allowedDomains = getAllowedDomains();

    if (urlObj.hostname === currentDomain) {
      return true;
    }

    return allowedDomains.some((allowedDomain) => {
      if (allowedDomain.startsWith('.')) {
        return urlObj.hostname.endsWith(allowedDomain);
      } else {
        return urlObj.hostname === allowedDomain;
      }
    });
  } catch {
    return false;
  }
};

/**
 * Handle post-login redirect with priority: URL param > sessionStorage
 * Returns true if redirect was handled, false for default navigation
 */
export const handlePostLoginRedirect = (
  navigate: NavigateFunction,
  searchParams: URLSearchParams,
): boolean => {
  const redirectParam = searchParams.get('redirect');
  const redirectFromSession = sessionStorage.getItem(SESSION_STORAGE_KEYS.REDIRECT_AFTER_LOGIN);

  if (redirectParam && isValidDxstarUrl(redirectParam)) {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
    navigate(redirectParam);
    return true;
  }

  if (redirectFromSession && isValidDxstarUrl(redirectFromSession)) {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
    navigate(redirectFromSession);
    return true;
  }

  if (redirectParam || redirectFromSession) {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
  }

  return false;
};
